import { isEmpty } from "lodash-es";

import {
  anyJsonSchemaType,
  EnumOptionsBE,
  JsonSchemaItems,
  PropertyTitleBE,
  PropertyTypeBE,
  PropertyTypeUI,
  PropertyValue,
  Schema,
  SchemaTypes,
  type SchemaTypesBE,
  SubSchema,
} from "src/api/flowTypes";
import {
  EventConfigEnriched,
  FeatureResponse,
  FeatureType,
} from "src/clients/features-control/api";
import { SchemaOptions } from "src/router/SearchParams";
import { isValidEnumOptionInput } from "src/schema/enumUtils";
import {
  decodeJsonRefPath,
  getEffectiveType,
  isJsonRefPath,
  JsonRefPath,
} from "src/schema/jsonRefUtils";
import { deepCopyWithoutKeys } from "src/utils/deepCopyWithoutKeys";
import { assertUnreachable } from "src/utils/typeUtils";

export const defaultSchema: Schema = {
  $schema: "https://json-schema.org/draft/2020-12/schema",
  properties: {},
  required: [],
  order: [],
  sensitive: [],
  type: "object",
};

export type EnumValueObject = { value: string };

// JSON schema property representation compatible with RHF (React Hook Form).
//
// Note: To be able to change keys in the JSON schema object in the UI,
// the key needs to be inlined as a fieldName here.
type BasePropertyUI = PropertyUIPassthrough & {
  fieldName: string;
  required: boolean;
  sensitive: boolean;
};

// Helper to define which parts of the backend type definition are passed-through to the frontend type definition
type PropertyUIPassthrough = Omit<
  PropertyValue,
  // Omit any conflicting keys
  | "type"
  | "anyOf"
  | "format"
  | "title"
  | "$ref"
  | "enum"
  | "required"
  | "sensitive"
>;

// Mixin type for retaining a reference to the original JSON schema after conversion
type WithSchemaReference<T> = {
  _schema?: Schema | SubSchema;
  _schemaNode?: T;
};

export type PropertyUI = BasePropertyUI & {
  type: PropertyTypeUI;
  enum?: EnumValueObject[];
  items?: JsonSchemaItems;
  _isNew?: boolean;
} & WithSchemaReference<PropertyValue>;

export type SchemaUI = {
  $schema: string;
  $defs?: Record<string, SubSchemaUI> | undefined;
  definitions?: Record<string, SubSchemaUI> | undefined; // same as "$defs", draft-07 and older
  properties: PropertyUI[];
  type: "object";
} & WithSchemaReference<Schema>;

export type SubSchemaUI<T = PropertyUI> = {
  properties: T[];
  type: PropertyTypeBE;
  enum?: EnumOptionsBE[];
} & WithSchemaReference<SubSchema>;

export type CommonSchemaEditRowProps = {
  rowName: `properties.${number}`;
  disabled: boolean;
};

export type SchemaTypeSelectorProps = CommonSchemaEditRowProps & {
  type: SchemaOptions;
  index: number;
};

const FIELD_NAME_PREFIX = "field_name_";

const getNewPropertyName = (existingProperties: PropertyUI[]) => {
  if (isEmpty(existingProperties)) {
    return "field_name";
  }

  const indexes = existingProperties
    .filter(({ fieldName }) => fieldName.startsWith(FIELD_NAME_PREFIX))
    .map(({ fieldName }) => fieldName.substring(FIELD_NAME_PREFIX.length))
    .map((index) => parseInt(index));

  const nextIndex = Math.max(...indexes, 0) + 1;
  return FIELD_NAME_PREFIX + nextIndex;
};

export const getNewProperty = (
  existingProperties: PropertyUI[],
): PropertyUI => {
  return {
    fieldName: getNewPropertyName(existingProperties),
    type: ["string", false],
    required: true,
    sensitive: false,
    _isNew: true,
  };
};

const comparePropertiesByOrder =
  (order: string[] | undefined) => (a: PropertyUI, b: PropertyUI) => {
    if (!order) {
      return 0; // Keep original order
    }
    const firstIndex = order.indexOf(a.fieldName);
    const secondIndex = order.indexOf(b.fieldName);
    return firstIndex - secondIndex;
  };

export class PropertyConverter {
  static readonly uiToBEEnum = (
    enumOptions: EnumValueObject[],
    nullable: boolean,
  ): EnumOptionsBE => {
    const beEnums: EnumOptionsBE = enumOptions.map(({ value: option }) => {
      // Check for boolean literals first
      if (option === "true") {
        return true;
      }
      if (option === "false") {
        return false;
      }

      const number = Number(option);
      if (!isNaN(number)) {
        return number;
      }

      if (isValidEnumOptionInput(option)) {
        return option.substring(1, option.length - 1);
      }

      return option;
    });
    if (nullable) {
      beEnums.push(null);
    }
    return beEnums;
  };

  static readonly beToUIEnum = (
    enumOptions: EnumOptionsBE,
  ): EnumValueObject[] => {
    return enumOptions
      .filter((option) => option !== null)
      .map((option) => {
        // This check is just to satisfy TS, in the filter above we make sure there are no nulls here
        if (option === null) {
          return { value: "" };
        }
        if (typeof option === "boolean") {
          return { value: String(option) };
        }
        if (typeof option === "number") {
          return { value: String(option) };
        }
        if (option.includes('"')) {
          return { value: `'${option}'` };
        }
        return { value: `"${option}"` };
      });
  };

  static readonly uiToBEProperty = (
    property: PropertyUI,
    schemaType: SchemaOptions,
  ): PropertyValue => {
    // Save original schema node before deleting (for custom types)
    const originalSchemaNode = property._schemaNode;
    const [propertyTypeValue] = property.type || [];

    // Delete original schema references from prior BE=>UI conversion (if any)
    property = deepCopyWithoutKeys(property, ["_schema", "_schemaNode"]);

    // Extract known PropertyUI fields to get passthrough properties
    const {
      fieldName: _fieldName,
      type: propertyType,
      required: _required,
      sensitive: _sensitive,
      enum: enumProp,
      items,
      properties,
      _isNew,
      ...passthroughProps
    } = property;

    // For custom types (real unions), preserve the original union structure
    if (propertyTypeValue === "union" && originalSchemaNode) {
      const isNullable = this.isNullable(property);

      // Handle nullable toggle for unions (anyOf/oneOf)
      const updatedSchemaNode = { ...originalSchemaNode };

      if ("anyOf" in updatedSchemaNode && updatedSchemaNode.anyOf) {
        const nonNullMembers = updatedSchemaNode.anyOf.filter(
          (member) => !("type" in member && member.type === "null"),
        );

        // Add or remove null based on nullable state
        updatedSchemaNode.anyOf = isNullable
          ? [...nonNullMembers, { type: "null" as const }]
          : nonNullMembers;
      } else if ("oneOf" in updatedSchemaNode && updatedSchemaNode.oneOf) {
        const nonNullMembers = updatedSchemaNode.oneOf.filter(
          (member) => !("type" in member && member.type === "null"),
        );

        // Add or remove null based on nullable state
        updatedSchemaNode.oneOf = isNullable
          ? [...nonNullMembers, { type: "null" as const }]
          : nonNullMembers;
      }

      return {
        ...passthroughProps,
        ...updatedSchemaNode,
        ...(enumProp ? { enum: this.uiToBEEnum(enumProp, isNullable) } : {}),
        ...(propertyType?.includes("array") ? { items: items ?? {} } : {}),
        ...(properties ? { properties: properties } : {}),
      };
    }

    const type: PropertyTypeBE | JsonRefPath = this.typeSetter(property);
    const isNullable = this.isNullable(property);

    if (isNullable && typeof type !== "string") {
      type.push("null");
    }

    let schemaTypeDef: PropertyValue;
    if (typeof type === "string") {
      schemaTypeDef = isNullable
        ? { anyOf: [{ $ref: type }, { type: "null" } as const] }
        : { $ref: type };
    } else {
      // When switching from custom to regular type, we need to exclude union-related fields
      const cleanedOriginalSchemaNode = originalSchemaNode
        ? deepCopyWithoutKeys(originalSchemaNode, [
            "anyOf",
            "oneOf",
            "allOf",
            "items", // re-added conditionally below when type needs it
            "format", // re-added explicitly by formatSetter()
          ])
        : {};

      schemaTypeDef = {
        ...cleanedOriginalSchemaNode, // recover inline object definitions from original backend schema (if any)
        // unset $ref if it's a scalar type
        $ref: isJsonRefPath(propertyTypeValue)
          ? cleanedOriginalSchemaNode.$ref
          : undefined,
        type,
      };
    }

    const format = this.formatSetter(property);
    const title =
      schemaType === SchemaOptions.Input
        ? this.titleSetter(property)
        : undefined;

    return {
      ...passthroughProps,
      ...schemaTypeDef,
      ...(format !== undefined ? { format } : {}),
      ...(title !== undefined ? { title } : {}),
      ...(enumProp ? { enum: this.uiToBEEnum(enumProp, isNullable) } : {}),
      ...(propertyType?.includes("array") || propertyTypeValue === "file[]"
        ? {
            items:
              propertyTypeValue === "file[]"
                ? { type: "string", format: "file-id" }
                : (items ?? {}),
          }
        : {}),
      ...(properties ? { properties: properties } : {}),
    };
  };
  /**
   * We're setting schema title to make sure that the backend knows that
   * it needs to transform the fields to native date/datetime types. This is
   * required for preserving backwards compatibility with the old date/datetime fields.
   */
  static readonly titleSetter = (property: PropertyUI) => {
    const [type] = property.type || [];
    if (type === "datetime") {
      return "native-datetime";
    }
    if (type === "date") {
      return "native-date";
    }
    return undefined;
  };

  static readonly formatSetter = (property: PropertyUI) => {
    const [type] = property.type || [];
    if (type === "datetime" || type === "datetime-str") {
      return "date-time";
    }
    if (type === "date" || type === "date-str") {
      return "date";
    }
    if (type === "file") {
      return "file-id";
    }
    return undefined;
  };

  static readonly isNullable = (property: PropertyUI) => {
    if (property.type == null || !Array.isArray(property.type)) {
      return false;
    }
    return property.type[1] === "null"; // always assume [type, nullable]
  };

  static readonly typeSetter = (
    property: PropertyUI,
  ): SchemaTypesBE[] | JsonRefPath => {
    const [propertyType] = property.type || [];
    if (typeof propertyType === "string" && isJsonRefPath(propertyType)) {
      return propertyType;
    }

    if (propertyType === "any") {
      return [...anyJsonSchemaType];
    }
    if (propertyType === "union") {
      return []; // Type will be constrained through oneOf/anyOf
    }
    if (propertyType === "enum") {
      const enumValues = this.uiToBEEnum(
        property.enum ?? [],
        this.isNullable(property),
      );
      return this.inferEnumType(enumValues) ?? ["string"];
    }

    if (
      propertyType === "datetime" ||
      propertyType === "date" ||
      propertyType === "datetime-str" ||
      propertyType === "date-str"
    ) {
      return ["string"];
    }

    if (propertyType === "file") {
      return ["string"];
    }
    if (propertyType === "file[]") {
      return ["array"];
    }

    return [propertyType];
  };

  static readonly inferEnumType = (
    enumValues: unknown[],
  ): SchemaTypesBE[] | undefined => {
    enumValues = enumValues.filter(
      // Ignore "null" as a reserved keyword here
      (enumValue) => enumValue != "null" && enumValue != null,
    );
    if (enumValues.length === 0) {
      return undefined;
    }

    const typeSet = new Set<SchemaTypesBE>();
    enumValues.forEach((val) => {
      if (typeof val === "number") {
        // For compatibility, always default to "number" and don't check for "integer"
        typeSet.add("number");
      } else if (typeof val === "string") {
        typeSet.add("string");
      } else if (typeof val === "boolean") {
        typeSet.add("boolean");
      }
    });

    return Array.from(typeSet).sort(); // sorted for consistency
  };

  static readonly beToUiProperty = (
    name: string,
    property: PropertyValue,
    schemaType: SchemaOptions,
    required: boolean,
    sensitive: boolean,
    schema: Schema | SubSchema,
  ): PropertyUI => {
    const { type, anyOf, oneOf, format, title, $ref, ...restProperties } =
      property;

    // Check if this is a real union (anyOf/oneOf with multiple non-nullable types)
    // Also check if we should use the anyOf logic for nullable handling
    const union = anyOf || oneOf;

    const isRealUnion = (() => {
      if (!union || union.length <= 1) return false;

      const nonNullMembers = union.filter(
        (member) => !("type" in member && member.type === "null"),
      );
      return nonNullMembers.length > 1;
    })();

    const fieldType =
      type ??
      union
        ?.map(({ type, $ref }) => ($ref ? $ref : type))
        .find((t) => t !== "null");

    const fieldNullable = (() => {
      if (($ref === undefined || !isJsonRefPath($ref)) && !union) {
        return this.nullableSetterBE(type);
      }

      return union?.some(({ type }) => type === "null") ? "null" : false;
    })();
    const anyOfNonNull = union?.find(({ type }) => type !== "null");
    const enumOptions =
      restProperties.enum ??
      restProperties.items?.enum ??
      anyOfNonNull?.enum ??
      anyOfNonNull?.items?.enum;

    const passthroughProps: PropertyUIPassthrough = restProperties;

    // Determine the field type
    let determinedType: SchemaTypes | JsonRefPath;
    if (isRealUnion) {
      determinedType = "union";
    } else if ($ref !== undefined && isJsonRefPath($ref)) {
      determinedType = $ref;
    } else if (
      // Detect file[] via items.format on array types
      restProperties.items?.format === "file-id" &&
      (fieldType === "array" ||
        (Array.isArray(fieldType) && fieldType.includes("array")))
    ) {
      determinedType = "file[]";
    } else {
      determinedType = this.typeSetterBE(
        fieldType,
        format,
        schemaType,
        title,
        enumOptions,
      );
    }

    return {
      ...passthroughProps,
      fieldName: name,
      type: [determinedType, fieldNullable],
      ...(enumOptions != null ? { enum: this.beToUIEnum(enumOptions) } : {}),
      required,
      sensitive,
      _schema: schema,
      _schemaNode: property,
    };
  };

  static readonly beToUiProperties = (
    schema: Schema | SubSchema,
    schemaType: SchemaOptions,
    { forceNativeDates = false }: { forceNativeDates?: boolean } = {},
  ): PropertyUI[] => {
    const getTitle = (property: PropertyValue) => {
      if (forceNativeDates) {
        const isDateFormat = property.format === "date";
        const isDateTimeFormat = property.format === "date-time";

        const isDateFormattedString =
          property.type?.includes("string") &&
          (isDateFormat || isDateTimeFormat);

        const format = isDateTimeFormat ? "native-datetime" : "native-date";
        return isDateFormattedString ? format : property.title;
      }

      return property.title;
    };
    const property: PropertyUI[] = Object.entries(schema.properties || {}).map(
      ([name, property]) =>
        this.beToUiProperty(
          name,
          {
            ...property,
            title: getTitle(property),
          },
          schemaType,
          "required" in schema ? schema.required!.includes(name) : false,
          "sensitive" in schema && schema.sensitive !== undefined
            ? schema.sensitive?.includes(name)
            : false,
          schema,
        ),
    );
    return property;
  };

  static readonly typeSetterBE = (
    type: PropertyTypeBE | JsonRefPath,
    format: string | undefined,
    schemaType: SchemaOptions,
    title: PropertyTitleBE,
    enumOptions: EnumOptionsBE | undefined,
  ): SchemaTypes | JsonRefPath => {
    if (typeof type === "string" && isJsonRefPath(type)) {
      return type;
    }
    const isEnumType =
      enumOptions != null &&
      (type === "string" ||
        type === "number" ||
        type === "boolean" ||
        type === undefined || // When type is not defined but enum exists
        // Compatibility code, when type is array of string, number, boolean (and/or "null")
        ((type?.includes("string") ||
          type?.includes("number") ||
          type?.includes("boolean")) &&
          type.length <= 3));
    if (isEnumType) {
      return "enum";
    }

    // File type detection via format: "file-id" (single file only; file[] is detected in beToUiProperty via items.format)
    if (format === "file-id") {
      if (
        type === "string" ||
        (Array.isArray(type) && type.includes("string"))
      ) {
        return "file";
      }
    }

    // Backward compatibility code, when type is just a string notation or undefined
    if (!type || typeof type === "string") {
      if (type === undefined && enumOptions == null) {
        return "any";
      }

      if (type === "string" && format === "date-time") {
        if (schemaType === SchemaOptions.Input) {
          if (title === "native-datetime") {
            return "datetime";
          }
          return "datetime-str";
        }
        // Output schema
        return "datetime";
      }

      if (type === "string" && format === "date") {
        if (schemaType === SchemaOptions.Input) {
          if (title === "native-date") {
            return "date";
          }
          return "date-str";
        }
        // Output schema
        return "date";
      }

      return type ?? "any";
    }

    // Type is an array of types
    // Filter out "null" type value, and take what's left
    const types = type.filter<SchemaTypesBE>(
      (t): t is SchemaTypesBE => t !== "null",
    );

    // If there are more than 1 type in array
    // It is only case for any type
    if (types.length > 1) {
      return "any";
    }

    if (types[0] === "string" && format === "date-time") {
      if (schemaType === SchemaOptions.Input) {
        if (title === "native-datetime") {
          return "datetime";
        }
        return "datetime-str";
      }
      // Output schema
      return "datetime";
    }

    if (types[0] === "string" && format === "date") {
      if (schemaType === SchemaOptions.Input) {
        if (title === "native-date") {
          return "date";
        }
        return "date-str";
      }
      // Output schema
      return "date";
    }

    return types[0];
  };

  static readonly nullableSetterBE = (type: PropertyTypeBE) => {
    if (Array.isArray(type)) {
      return type.includes("null") ? "null" : false;
    }

    if (type === undefined) {
      // Legacy `any` type, nullable by default
      return "null";
    }

    return false;
  };
}

export class SchemaConverter {
  static uiToBE(schema: SchemaUI, schemaType: SchemaOptions): Schema {
    // Note: We need to deep copy to avoid mutating the original schema.
    // We don't delete _schema and _schemaNode yet because uiToBEProperty needs them for preserving real unions.
    schema = structuredClone(schema);

    // Filter out properties with empty names
    if (schema.properties && Array.isArray(schema.properties)) {
      schema.properties = schema.properties.filter(
        (property) => property.fieldName && property.fieldName.trim() !== "",
      );
    }

    const convertedSchema: Schema = {
      $schema: schema.$schema,
      type: schema.type,
      properties: {},
      required: [],
      sensitive: [],
      order: [],
    };

    // Schema properties
    schema.properties.forEach((property) => {
      convertedSchema.properties = {
        ...convertedSchema.properties,
        [property.fieldName]: PropertyConverter.uiToBEProperty(
          property,
          schemaType,
        ),
      };
      if (property.required) {
        convertedSchema.required!.push(property.fieldName);
      }
      // maintain schema order
      convertedSchema.order!.push(property.fieldName);
      if (property.sensitive && convertedSchema.sensitive) {
        convertedSchema.sensitive.push(property.fieldName);
      }
    });

    // Subschemas ($defs)
    const uiDefinitions = schema.$defs ?? schema.definitions;
    if (uiDefinitions) {
      const beDefinitions = Object.fromEntries(
        Object.entries(uiDefinitions).map(([subSchemaKey, subSchema]) => {
          // Delete original schema references from prior BE=>UI conversion (if any)
          subSchema = deepCopyWithoutKeys(subSchema, [
            "_schema",
            "_schemaNode",
          ]);

          // Handle both UI format (properties as array) and BE format (properties as object)
          const propertiesIsArray = Array.isArray(subSchema.properties);

          // If properties is already in BE format (object), pass through as-is
          if (!propertiesIsArray) {
            return [subSchemaKey, subSchema];
          }

          const isObject = subSchema.properties.length > 0;
          const convertedSubSchema: SubSchema = {
            ...subSchema,
            properties: isObject ? {} : undefined,
            required: isObject ? [] : undefined,
            order: isObject ? [] : undefined,
            sensitive: isObject ? [] : undefined,
          };

          subSchema.properties.forEach((property) => {
            convertedSubSchema.properties = {
              ...convertedSubSchema.properties,
              [property.fieldName]: PropertyConverter.uiToBEProperty(
                property,
                schemaType,
              ),
            };
            if (property.required) {
              convertedSubSchema.required!.push(property.fieldName);
            }
            convertedSubSchema.order!.push(property.fieldName);
            if (property.sensitive && convertedSubSchema.sensitive) {
              convertedSubSchema.sensitive.push(property.fieldName);
            }
          });

          return [subSchemaKey, convertedSubSchema];
        }),
      );
      if (schema.$defs) {
        convertedSchema.$defs = beDefinitions;
      } else if (schema.definitions) {
        convertedSchema.definitions = beDefinitions;
      }
    }

    return convertedSchema;
  }

  static beToUI(schema: Schema, schemaType: SchemaOptions): SchemaUI {
    const convertedProperties: PropertyUI[] =
      PropertyConverter.beToUiProperties(schema, schemaType);

    const sortedProperties = [...convertedProperties].sort(
      comparePropertiesByOrder(schema.order),
    );

    const defsSubSchemas = Object.fromEntries(
      Object.entries(schema.$defs ?? schema.definitions ?? {}).map(
        ([subSchemaKey, subSchema]) => {
          return [
            subSchemaKey,
            {
              ...subSchema,
              properties: PropertyConverter.beToUiProperties(
                subSchema,
                SchemaOptions.Input,
                // We always use native dates for newest schemas
                { forceNativeDates: true },
              ),
              _schema: schema,
              _schemaNode: subSchema,
            },
          ];
        },
      ),
    );

    return {
      $schema: schema.$schema,
      ...(schema.$defs != null ? { $defs: defsSubSchemas } : {}),
      ...(schema.definitions != null ? { definitions: defsSubSchemas } : {}),
      type: schema.type,
      properties: sortedProperties,
      _schema: schema,
      _schemaNode: schema,
    };
  }
}

const globalFeaturesKey = "FeaturesGlobal" as const;

type FeaturesKey = `Features@${string}` | typeof globalFeaturesKey;
export const entityIdToFeaturesKey = (entityId: string) =>
  `Features@${entityId}` as FeaturesKey;

// Also used as a display helper
export const getFeaturesByEntity = (
  features: FeatureResponse[],
): Record<FeaturesKey, FeatureResponse[]> => {
  return features.reduce(
    (acc: Record<string, FeatureResponse[]>, feature) => {
      if (
        // isGlobalFeature
        feature.entity_types === undefined ||
        feature.entity_types === null ||
        feature.entity_types.length === 0
      ) {
        if (globalFeaturesKey in acc) {
          acc[globalFeaturesKey].push(feature);
        } else {
          acc[globalFeaturesKey] = [feature];
        }
      } else {
        if (entityIdToFeaturesKey(feature.entity_types[0]) in acc) {
          acc[entityIdToFeaturesKey(feature.entity_types[0])].push(feature);
        } else {
          acc[entityIdToFeaturesKey(feature.entity_types[0])] = [feature];
        }
      }
      return acc;
    },
    {} as Record<string, FeatureResponse[]>,
  );
};

const featureListToJsonSchema = (features: FeatureResponse[]): SubSchema => {
  const props = features.reduce(
    (acc, feature) => {
      const featureType: SchemaTypes = (() => {
        if (!feature.feature_type) {
          return "any";
        }

        switch (feature.feature_type) {
          case FeatureType.NUMBER:
            return "number";
          case FeatureType.BOOLEAN:
            return "boolean";
          case FeatureType.STRING:
            return "string";
          case FeatureType.DATETIME:
            return "datetime";
          case FeatureType.DATE:
            return "date";
          case FeatureType.ARRAY:
            return "array";
          case FeatureType.OBJECT:
            return "object";
          default:
            assertUnreachable(feature.feature_type);
            return "any";
        }
      })();

      const beProperty = PropertyConverter.uiToBEProperty(
        {
          fieldName: feature.key,
          required: false,
          sensitive: false,
          type: [
            featureType,
            feature.evaled_feature_default === null ? "null" : false,
          ],
        },
        SchemaOptions.Input,
      );

      return {
        ...acc,
        [feature.key]: {
          ...beProperty,
          default: feature.evaled_feature_default,
        },
      };
    },
    {} as SubSchema["properties"],
  );
  return {
    properties: props,
    type: "object",
  };
};

export const buildSubSchemas = (
  schema: SchemaUI,
  eventSchemas: EventConfigEnriched[],
  entitySchemas: Record<string, SubSchema>,
  features: FeatureResponse[],
) => {
  const hasEnrichedProperty = schema.properties.some((property) =>
    isJsonRefPath(getEffectiveType(property)),
  );
  const acc: Record<string, SubSchema> = hasEnrichedProperty
    ? entitySchemas
    : {};

  const featuresByEntity = getFeaturesByEntity(features);

  // Always add features definitions
  Object.entries(featuresByEntity).forEach(([key, value]) => {
    if (value) {
      acc[key] = featureListToJsonSchema(value);
    }
  });

  // Add events to the acc
  return schema.properties.reduce((acc, property) => {
    const propertyEffectiveType = getEffectiveType(property);
    if (isJsonRefPath(propertyEffectiveType)) {
      const decoded = decodeJsonRefPath(propertyEffectiveType);
      if (!decoded) return acc;
      const { metaType, type } = decoded;
      if (metaType === "entity") {
        // Features for all entities are already added to the acc
        return acc;
      }
      if (metaType === "event") {
        const eventSchema = eventSchemas?.find(
          (event) => event.event_type === type,
        );
        if (eventSchema) {
          const eventJsonSchema = eventSchema.json_schema;
          // The $defs of an event carry all entities referenced in the event schema.
          // But since we already add all entities to the schema by default we can just delete the $def here.
          const eventJsonSchemaWithoutDefs = {
            ...eventJsonSchema,
          };
          // @ts-expect-error (Will not break if $defs is not present)
          delete eventJsonSchemaWithoutDefs?.$defs;
          return {
            ...acc,
            [`Events@${type}`]: eventJsonSchemaWithoutDefs as SubSchema,
          };
        }
      }
    }
    return acc;
  }, acc);
};
